"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from "@/lib/utils";
import { Button, Chip, Icon, Menu, MenuItem, Select, Typography } from "@bookips/sds";
import { useCallback } from "react";
import { stepCodeLookup } from "./ProductListItem";
/** step 필터 아이템 표시 텍스트 변환 */ const getFilterItemLabel = (filterKey, item)=>{
    if (filterKey === "steps") {
        const stepNumber = Number(item);
        return stepCodeLookup[stepNumber] || item;
    }
    return item;
};
const FilterChipContainer = (param)=>{
    let { filterItems , selectedFilters , onChipDelete , onReset  } = param;
    // 선택된 필터가 있는지 확인
    const hasAnySelection = Object.values(selectedFilters).some((values)=>values.length > 0);
    if (!hasAnySelection) {
        return null;
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-gray-0 -mx-5 flex items-start justify-between gap-5 rounded-md px-5 py-3 sm:mx-0",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex min-h-[32px] flex-1 flex-wrap items-center gap-2",
                children: filterItems.map((filter)=>{
                    const values = selectedFilters[filter.key] || [];
                    return values.map((value)=>/*#__PURE__*/ _jsx(Chip, {
                            label: getFilterItemLabel(filter.key, value),
                            onDelete: ()=>onChipDelete(filter.key, value)
                        }, "".concat(filter.key, "-").concat(value)));
                })
            }),
            /*#__PURE__*/ _jsx(Button, {
                className: "p-1.5 sm:hidden",
                variant: "stroke",
                size: "sm",
                color: "secondary",
                onClick: onReset,
                children: /*#__PURE__*/ _jsx(Icon.Reset_2px, {
                    className: "text-primary size-5"
                })
            }),
            /*#__PURE__*/ _jsx(Button, {
                className: "hidden sm:flex",
                variant: "stroke",
                size: "sm",
                color: "secondary",
                leftElement: /*#__PURE__*/ _jsx(Icon.Reset_2px, {
                    className: "text-primary size-3.5"
                }),
                onClick: onReset,
                children: "초기화"
            })
        ]
    });
};
/**
 * 상품 필터 컴포넌트
 * - 마지막 depth 카테고리에서 사용
 * - 단원, 자료유형, 제작자 필터
 * - 체크박스 multi-select 지원
 * - 모바일(sm 이하)에서는 버튼으로 사이드시트 열기
 */ export const ProductFilter = (param)=>{
    let { className , filterItems , selectedFilters , onFilterChange , onReset , onMobileFilterOpen , isLoading  } = param;
    // 로딩 중이거나 filterItems가 없으면 스켈레톤 표시
    const showSkeleton = isLoading || !filterItems || filterItems.length === 0;
    // 필터 값 토글 (선택/해제)
    const handleFilterToggle = useCallback((filterKey, value)=>{
        const currentValues = selectedFilters[filterKey] || [];
        const isSelected = currentValues.includes(value);
        if (isSelected) {
            onFilterChange(filterKey, currentValues.filter((v)=>v !== value));
        } else {
            onFilterChange(filterKey, [
                ...currentValues,
                value
            ]);
        }
    }, [
        selectedFilters,
        onFilterChange
    ]);
    // 칩 삭제 핸들러
    const handleChipDelete = useCallback((filterKey, value)=>{
        const currentValues = selectedFilters[filterKey] || [];
        onFilterChange(filterKey, currentValues.filter((v)=>v !== value));
    }, [
        selectedFilters,
        onFilterChange
    ]);
    // 필터 초기화 (외부에서 전달된 onReset 사용, 없으면 내부 로직)
    const handleReset = useCallback(()=>{
        if (onReset) {
            onReset();
        } else {
            filterItems.forEach((filter)=>{
                onFilterChange(filter.key, []);
            });
        }
    }, [
        filterItems,
        onFilterChange,
        onReset
    ]);
    // 드롭다운형 필터 (단원, 자료유형 등 - items가 2개 이상인 필터)
    const dropdownFilters = filterItems.filter((filter)=>(filter.items.length > 1 || filter.key === "steps" && filter.items.length > 0) && filter.key !== "solvookPasses");
    // 쏠북패스 필터 (토글 버튼으로 표시, items가 있을 때만 노출)
    const solvookPassFilter = filterItems.find((filter)=>filter.key === "solvookPasses" && filter.items.length > 0);
    // 토글 필터 클릭 핸들러
    const handleToggleFilter = useCallback((filterKey, filterName)=>{
        const currentValues = selectedFilters[filterKey] || [];
        const isActive = currentValues.length > 0;
        onFilterChange(filterKey, isActive ? [] : [
            filterName
        ]);
    }, [
        selectedFilters,
        onFilterChange
    ]);
    // 표시할 필터가 없으면 렌더링하지 않음
    if (!showSkeleton && dropdownFilters.length === 0 && !solvookPassFilter) {
        return null;
    }
    if (showSkeleton) {
        return /*#__PURE__*/ _jsxs("div", {
            className: cn("flex flex-col gap-3", className),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "flex flex-wrap items-center gap-2 sm:hidden",
                    children: Array.from({
                        length: 3
                    }).map((_, index)=>/*#__PURE__*/ _jsx("div", {
                            className: "h-10 w-20 animate-pulse rounded-md bg-gray-200"
                        }, index))
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "hidden flex-wrap items-center gap-2 sm:flex",
                    children: Array.from({
                        length: 3
                    }).map((_, index)=>/*#__PURE__*/ _jsx("div", {
                            className: "h-10 w-[120px] animate-pulse rounded-md bg-gray-200"
                        }, index))
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: cn("flex flex-col gap-3", className),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "scrollbar-hide -mx-5 flex items-center gap-2 overflow-x-auto px-5 sm:hidden",
                children: [
                    solvookPassFilter && /*#__PURE__*/ _jsx(Button, {
                        variant: "stroke",
                        size: "md",
                        color: (selectedFilters[solvookPassFilter.key] || []).length > 0 ? "primary" : "secondary",
                        onClick: ()=>handleToggleFilter(solvookPassFilter.key, solvookPassFilter.items[0]),
                        className: "shrink-0",
                        children: solvookPassFilter.name
                    }),
                    dropdownFilters.map((filter)=>{
                        const selectedValues = selectedFilters[filter.key] || [];
                        const hasSelection = selectedValues.length > 0;
                        return /*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            onClick: ()=>{
                                return onMobileFilterOpen === null || onMobileFilterOpen === void 0 ? void 0 : onMobileFilterOpen(filter.key);
                            },
                            className: cn("flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-2.5 whitespace-nowrap", hasSelection ? "border-gray-700 shadow-sm" : "border-gray-200"),
                            children: [
                                /*#__PURE__*/ _jsxs(Typography, {
                                    variant: "body2",
                                    className: "text-primary",
                                    children: [
                                        filter.name,
                                        hasSelection && " ".concat(selectedValues.length)
                                    ]
                                }),
                                hasSelection ? /*#__PURE__*/ _jsx(Icon.ArrowUp_2px, {
                                    className: "text-primary size-3.5"
                                }) : /*#__PURE__*/ _jsx(Icon.ArrowDown_2px, {
                                    className: "text-primary size-3.5"
                                })
                            ]
                        }, filter.key);
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "hidden flex-wrap items-center gap-2 sm:flex",
                children: [
                    solvookPassFilter && /*#__PURE__*/ _jsx(Button, {
                        variant: "stroke",
                        size: "md",
                        color: (selectedFilters[solvookPassFilter.key] || []).length > 0 ? "primary" : "secondary",
                        onClick: ()=>handleToggleFilter(solvookPassFilter.key, solvookPassFilter.items[0]),
                        children: solvookPassFilter.name
                    }),
                    dropdownFilters.map((filter)=>{
                        const selectedValues = selectedFilters[filter.key] || [];
                        return /*#__PURE__*/ _jsx(Select, {
                            size: "md",
                            placeholder: filter.name,
                            multiple: true,
                            count: selectedValues.length,
                            onValueChange: (value)=>handleFilterToggle(filter.key, value),
                            children: /*#__PURE__*/ _jsx(Menu, {
                                typo: "body2",
                                scroll: true,
                                maxHeight: 376,
                                minWidth: 400,
                                children: filter.items.map((item)=>/*#__PURE__*/ _jsx(MenuItem, {
                                        value: item,
                                        typo: "body2",
                                        multiple: true,
                                        selected: selectedValues.includes(item),
                                        children: getFilterItemLabel(filter.key, item)
                                    }, item))
                            })
                        }, filter.key);
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(FilterChipContainer, {
                filterItems: filterItems,
                selectedFilters: selectedFilters,
                onChipDelete: handleChipDelete,
                onReset: handleReset
            })
        ]
    });
};
